import { strokeTypeToBrushType } from "../../neosmartpen/pencomm/pencomm";
import { IBrushType } from "../enums";
import { NEO_SMARTPEN_TYPE } from "../neopen";
import { uuidv4 } from "../util";
import { DOT_TYPE, NeoDot } from "./NeoDot";
export var StrokeStatus;
(function (StrokeStatus) {
    StrokeStatus["NORMAL"] = "normal";
    StrokeStatus["ERASED"] = "erased";
})(StrokeStatus || (StrokeStatus = {}));
export var StrokePageAttr;
(function (StrokePageAttr) {
    StrokePageAttr["SINGLEPAGE"] = "singlepage";
    StrokePageAttr["MULTIPAGE"] = "multipage";
})(StrokePageAttr || (StrokePageAttr = {}));
export class NeoStroke {
    get avr_force() {
        return this.sum_force / this.dotCount;
    }
    get avr_speed() {
        return this.sum_dist / this.duration;
    }
    /**
     *
     * @param {string} [mac]
     */
    constructor(props) {
        this.max_force = Number.MIN_VALUE;
        this.min_force = Number.MAX_VALUE; // 1
        this.sum_force = 0;
        this.max_speed = Number.MIN_VALUE; // 1
        this.min_speed = Number.MAX_VALUE; // 1
        this.sum_dist = 0;
        this.dotCount = 0;
        this.dotArray = [];
        this.opened = true;
        // 2022/11/29
        this.pathSVG = undefined;
        // 2022/11/29
        this.pathDataArr = {};
        this.boundingBox = null;
        /**
         *
         * @param {NeoDot} dot
         */
        this.addDot = (dot) => {
            // 필기 압력 처리
            this.min_force = Math.min(dot.f, this.min_force);
            this.max_force = Math.max(dot.f, this.max_force);
            this.sum_force += dot.f;
            // bounding box 처리
            if (!this.boundingBox) {
                this.boundingBox = {
                    left: dot.x,
                    top: dot.y,
                    right: dot.x,
                    bottom: dot.x
                };
            }
            else {
                if (dot.x < this.boundingBox.left)
                    this.boundingBox.left = dot.x;
                else if (dot.x > this.boundingBox.right)
                    this.boundingBox.right = dot.x;
                if (dot.y < this.boundingBox.top)
                    this.boundingBox.top = dot.y;
                else if (dot.y > this.boundingBox.bottom)
                    this.boundingBox.bottom = dot.y;
            }
            // count
            this.dotArray.push(dot);
            this.dotCount++;
        };
        this.close = () => {
            this.opened = false;
        };
        this.set = (properties) => {
            Object.keys(properties).forEach(key => {
                this[key] = properties[key];
            });
        };
        // from INeoStrokeProps
        if (props.originalKey)
            this.key = props.originalKey;
        else
            this.key = uuidv4();
        for (const k of Object.keys(props)) {
            this[k] = props[k];
        }
    }
}
/**
 *
 * @static
 * @param {{ stroke: InkstoreStroke, pageInfo: IPageSOBP }} { stroke, pageInfo }
 * @memberof NeoStroke
 *
 *
 * dot blob 구조체
 *
 *    const dotPacketSize = dotBlob.length / dotCount; // 16 or 17
 *    const shiftDot = dotPacketSize === 17 ? 1 : 0;
 *
 *    |00|01|02|03|04|05|06|07|08|09|10|11|12|13|14|15|
 *    |dt|force float|  x float  |  y flot   |        |            if dotPacketSize === 16
 *
 *    |00|01|02|03|04|05|06|07|08|09|10|11|12|13|14|15|16|
 *    |  |dt|force float|  x float  |  y flot   |        |         if dotPacketSize === 17
 */
NeoStroke.fromInkstoreStroke = (args) => {
    const stroke = args.stroke;
    const pageInfo = stroke.pageInfo ? stroke.pageInfo : args.pageInfo;
    const { dotBlob, dotPacketSize, shiftDot } = getInkstoreDotBlob(stroke);
    const view = new DataView(dotBlob.buffer);
    const s1 = ("00000000" + (0xff000000 ^ stroke.color).toString(16));
    const strColor = "#" + s1.substring(s1.length - 6);
    // 스트로크 생성
    const brushType = strokeTypeToBrushType(stroke.strokeType);
    const props = {
        mac: stroke.mac,
        color: strColor,
        startTime: stroke.startTime,
        thickness: stroke.thickness,
        penTipMode: stroke.penTipType,
        brushType,
        inputType: NEO_SMARTPEN_TYPE.INKSTORE_PEN,
        section: pageInfo.section,
        owner: pageInfo.owner,
        book: pageInfo.book,
        page: pageInfo.page,
        status: brushType === IBrushType.ERASER ? StrokeStatus.ERASED : StrokeStatus.NORMAL,
        writerId: stroke.writeId,
        originalKey: stroke.strokeUUID
    };
    const neoStroke = new NeoStroke(props);
    const dotCount = stroke.dotCount;
    const startTime = stroke.startTime;
    // 최초의 1점
    // const d = dotBlob.slice(0, dotPacketSize);
    const initialForce = view.getFloat32(1 + shiftDot, true);
    const x0 = view.getFloat32(5 + shiftDot, true);
    const y0 = view.getFloat32(9 + shiftDot, true);
    const initialDot = new NeoDot({
        dotType: DOT_TYPE.pendown,
        x: x0, y: y0, f: initialForce,
        deltaTime: 0,
        time: startTime,
    });
    neoStroke.addDot(initialDot);
    if (stroke.sum_dist_nu) {
        // 이미 속도 계산을 할 수 있는 상태라면
        fillStrokefromInkstoreStroke_without_speed(neoStroke, initialDot, view, shiftDot, dotPacketSize, dotCount);
    }
    else {
        // 속도 계산이 필요한 상태라면
        fillStrokefromInkstoreStroke_with_speed(neoStroke, initialDot, view, shiftDot, dotPacketSize, dotCount);
    }
    return neoStroke;
};
function fillStrokefromInkstoreStroke_without_speed(neoStroke, initialDot, view, shiftDot, dotPacketSize, dotCount) {
    const startTime = initialDot.time;
    // 중간 점과 끝 점
    let sum_deltaTime = 0;
    for (let i = 1; i < dotCount; i++) {
        const st = i * dotPacketSize;
        const f = view.getFloat32(st + 1 + shiftDot, true);
        const x = view.getFloat32(st + 5 + shiftDot, true);
        const y = view.getFloat32(st + 9 + shiftDot, true);
        const deltaTime = view.getUint8(st);
        sum_deltaTime += deltaTime;
        const dot = new NeoDot({
            dotType: i === dotCount - 1 ? DOT_TYPE.penup : DOT_TYPE.penmove,
            x, y, f,
            deltaTime,
            time: startTime + sum_deltaTime,
        });
        neoStroke.addDot(dot);
    }
    return neoStroke;
}
function fillStrokefromInkstoreStroke_with_speed(neoStroke, initialDot, view, shiftDot, dotPacketSize, dotCount) {
    const startTime = initialDot.time;
    // 중간 점과 끝 점
    let sum_deltaTime = 0;
    let { f: force_sum, x: x0, y: y0 } = initialDot;
    for (let i = 1; i < dotCount; i++) {
        const st = i * dotPacketSize;
        const f = view.getFloat32(st + 1 + shiftDot, true);
        force_sum += f;
        const x = view.getFloat32(st + 5 + shiftDot, true);
        const y = view.getFloat32(st + 9 + shiftDot, true);
        const deltaTime = view.getUint8(st);
        sum_deltaTime += deltaTime;
        const dot = new NeoDot({
            dotType: i === dotCount - 1 ? DOT_TYPE.penup : DOT_TYPE.penmove,
            x, y, f,
            deltaTime,
            time: startTime + sum_deltaTime,
        });
        neoStroke.addDot(dot);
        const dx = x - x0;
        const dy = y - y0;
        const dist = Math.sqrt(dx * dx + dy * dy);
        neoStroke.sum_dist += dist;
        if (deltaTime > 0) {
            const speed = dist / (deltaTime / 1000);
            neoStroke.max_speed = Math.max(speed, neoStroke.max_speed);
            neoStroke.min_speed = Math.min(speed, neoStroke.min_speed);
        }
        x0 = x;
        y0 = y;
    }
    neoStroke.duration = sum_deltaTime;
    neoStroke.endTime = startTime + sum_deltaTime;
    neoStroke.sum_force = force_sum;
    return neoStroke;
}
function getInkstoreDotBlob(stroke) {
    const dots = stroke.dots;
    const dotCount = stroke.dotCount;
    let dotBlob = new Uint8Array(0);
    if (typeof dots === "string") {
        // console.log(`string type dots data`);
        const binary_string = window.atob(dots);
        const len = binary_string.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        dotBlob = bytes;
    }
    else {
        // console.log(`binary type dots data`);
        dotBlob = dots.toUint8Array();
    }
    const dotPacketSize = dotBlob.length / dotCount; // 16 or 17
    let shiftDot = 0;
    if (dotPacketSize !== 16 && dotPacketSize !== 17) {
        console.error("invalid dot", stroke);
    }
    if (dotPacketSize === 17) {
        shiftDot = 1;
    }
    return { dotBlob, dotPacketSize, shiftDot };
}
//# sourceMappingURL=NeoStroke.js.map